/**
 * Who works on which branch — so two people sending commands to the same repository on different
 * branches find out before their agents' work collides.
 */

import type { LiveSession } from './types'

export interface BranchPeer {
  developerId: string
  developerName: string
  branch: string
}

const same = (a: string | null | undefined, b: string | null | undefined) => (a ?? '').toLowerCase() === (b ?? '').toLowerCase()

/**
 * The people whose agents work in `repository` right now on a branch other than `branch`, one line
 * per person and branch. Finished sessions and sessions with no branch reported do not count.
 */
export function othersOnOtherBranches(sessions: LiveSession[], repository: string, branch: string | null, me?: string | null): BranchPeer[] {
  if (!branch) return []
  const seen = new Set<string>()
  const peers: BranchPeer[] = []
  for (const session of sessions) {
    if (session.isTerminal || !session.branch || !same(session.repositoryName, repository)) continue
    if (same(session.branch, branch) || (me && session.developerId === me)) continue
    const key = `${session.developerId}|${session.branch}`
    if (seen.has(key)) continue
    seen.add(key)
    peers.push({ developerId: session.developerId, developerName: session.developerName, branch: session.branch })
  }
  return peers
}

/**
 * For each live session, the other branches of its repository that other live sessions are on —
 * empty when everybody in that repository is on the same branch.
 */
export function branchSplits(sessions: LiveSession[]): Map<string, BranchPeer[]> {
  const result = new Map<string, BranchPeer[]>()
  for (const session of sessions) {
    if (session.isTerminal || !session.repositoryName) continue
    result.set(session.workSessionId, othersOnOtherBranches(sessions, session.repositoryName, session.branch, session.developerId))
  }
  return result
}
